/**
 * XML tokenising for the item's XML tab, kept apart from the component so it can be tested.
 *
 * Hand-rolled rather than a highlighter library, for two reasons. The UI is served from CloudFront and
 * deliberately makes no external request on first paint — Open Sans is self-hosted via fontsource for
 * exactly that reason — so a CDN-loaded highlighter is not an option. And the bundle is already 1.08 MB
 * with a chunk-size warning, where a general-purpose highlighter costs tens of kilobytes to colour one
 * dialect of one language.
 *
 * ADI is a narrow target: a prolog, elements with attributes, and attribute values. No text nodes to
 * speak of, no CDATA, no processing instructions beyond the prolog.
 *
 * The property that matters is losslessness: concatenating the tokens must reproduce the input exactly.
 * This renders the document an operator checks before approving an export, so a highlighter that drops
 * a character is editing that document on screen. Unrecognised input falls through to a single plain
 * character rather than being skipped, and a test asserts the round trip.
 */

/** Ordered alternatives; the first to match at a position wins. */
const TOKENS: { kind: Kind; pattern: RegExp }[] = [
  { kind: 'prolog', pattern: /^<\?[\s\S]*?\?>/ },
  { kind: 'comment', pattern: /^<!--[\s\S]*?-->/ },
  { kind: 'tag', pattern: /^<\/?[A-Za-z_][\w.:-]*/ },
  { kind: 'tagEnd', pattern: /^\/?>/ },
  { kind: 'attribute', pattern: /^[A-Za-z_][\w.:-]*(?=\s*=)/ },
  { kind: 'operator', pattern: /^=/ },
  { kind: 'value', pattern: /^"[^"]*"|^'[^']*'/ },
  { kind: 'plain', pattern: /^\s+/ },
];

export type Kind =
  | 'prolog'
  | 'comment'
  | 'tag'
  | 'tagEnd'
  | 'attribute'
  | 'operator'
  | 'value'
  | 'plain';

/**
 * Colours chosen to sit on the near-black surface and stay distinct from the diff's own red/green,
 * which colours whole rows in the same view — a token colour that reads as "removed" would be actively
 * misleading.
 */
export const COLOUR: Record<Kind, string> = {
  prolog: 'text.disabled',
  comment: 'text.disabled',
  tag: '#7fb2ff',
  tagEnd: '#7fb2ff',
  attribute: '#c9a6ff',
  operator: 'text.secondary',
  value: '#f0c674',
  plain: 'text.primary',
};

export const tokenise = (line: string) => {
  const tokens: { kind: Kind; text: string }[] = [];
  let rest = line;

  while (rest.length > 0) {
    const match = TOKENS.reduce<{ kind: Kind; text: string } | undefined>(
      (found, candidate) => {
        if (found) {
          return found;
        }
        const hit = candidate.pattern.exec(rest);
        return hit ? { kind: candidate.kind, text: hit[0] } : undefined;
      },
      undefined,
    );

    if (match) {
      tokens.push(match);
      rest = rest.slice(match.text.length);
      continue;
    }

    // Nothing recognised at this position: take one character as plain text and carry on. This is what
    // keeps the output lossless — the alternative, skipping, silently edits the document on screen.
    tokens.push({ kind: 'plain', text: rest[0] });
    rest = rest.slice(1);
  }

  return tokens;
};
